import type { ReactNode } from 'react';
import { Box, IconButton, Tooltip, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';

// -----------------------------------------------------------------------------------
// Etiqueta de campo con ayuda contextual opcional.
//
// Renderiza el label POR ENCIMA del campo (no como label flotante de MUI) seguido del
// icono "i" en círculo. El tooltip aparece al pasar el ratón o al enfocar el icono con
// el teclado.
//
// Se renderiza fuera del input a propósito: los textos de ayuda del golden path de IaC
// son largos y un label flotante los recorta al hacer foco. Como contrapartida, los
// campos que lo usan NO deben declarar además su propio `label`, o el texto saldría
// duplicado.
//
// `tooltip` es opcional: sin él solo se pinta el label, sin icono. Eso permite que un
// mismo formulario mezcle campos con y sin ayuda sin ramificar en el llamante.
// -----------------------------------------------------------------------------------

const useStyles = makeStyles(theme => ({
  root: {
    // Aproxima la separación que MUI deja entre un InputLabel y su input.
    marginBottom: theme.spacing(0.5),
  },
  label: {
    color: theme.palette.text.secondary,
    lineHeight: 1.4,
  },
  required: {
    color: theme.palette.error.main,
    marginLeft: 2,
  },
  iconButton: {
    // Sin padding el icono se separa demasiado del texto del label.
    padding: theme.spacing(0.25),
    marginLeft: theme.spacing(0.25),
  },
  icon: {
    fontSize: '1rem',
    color: theme.palette.text.secondary,
    opacity: 0.7,
  },
  tooltip: {
    // Los textos de ayuda son largos: sin un ancho máximo el tooltip se estira a lo
    // ancho de la ventana y se vuelve ilegible.
    maxWidth: 420,
    fontSize: theme.typography.pxToRem(12),
    lineHeight: 1.5,
  },
}));

export interface LabelWithTooltipProps {
  /** Texto de la etiqueta del campo. */
  label: ReactNode;
  /** Ayuda contextual. Si se omite, no se muestra el icono. */
  tooltip?: ReactNode;
  /** Añade el asterisco de campo obligatorio, como hace MUI. */
  required?: boolean;
  /** Posición del tooltip respecto al icono. */
  placement?: 'top' | 'right' | 'bottom' | 'left';
}

export const LabelWithTooltip = ({
  label,
  tooltip,
  required = false,
  placement = 'top',
}: LabelWithTooltipProps) => {
  const classes = useStyles();

  return (
    <Box display="flex" alignItems="center" className={classes.root}>
      <Typography variant="body2" component="span" className={classes.label}>
        {label}
        {required && (
          <span aria-hidden="true" className={classes.required}>
            *
          </span>
        )}
      </Typography>

      {tooltip && (
        <Tooltip
          title={tooltip}
          placement={placement}
          arrow
          interactive
          enterDelay={200}
          classes={{ tooltip: classes.tooltip }}
        >
          {/* IconButton (y no un span) para que el tooltip sea alcanzable con el
              teclado. ButtonBase ya emite type="button", así que no envía el
              formulario del scaffolder al pulsarlo. */}
          <IconButton
            className={classes.iconButton}
            disableRipple
            aria-label={
              typeof label === 'string' ? `Más información sobre ${label}` : 'Más información'
            }
          >
            <InfoOutlinedIcon className={classes.icon} />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
};
